import type { StockListResponseType } from "@sellar-io/api-v2/types";
import { pluralize } from "@sellar-io/common";
import {
  DataButton,
  H4,
  H5,
  getDndKitSortableIndexes,
} from "@sellar-io/components";
import { DragDropProvider } from "@dnd-kit/react";
import type { ComponentProps, ReactElement } from "react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Icon, SpinnerLegacy } from "../../../../../components";
import { Alert } from "../../../../../components/alert/Alert";
import { NavigationBar } from "../../../../../components/navigation/NavigationBar";
import { LAYOUT_GROUPING } from "../../../../../constants/routes";
import { useProductReOrder } from "../../../../../hooks/api/v2/product/useProductReOrder";
import { useProductUngrouped } from "../../../../../hooks/api/v2/product/useUngroupedProducts";
import { useStockList } from "../../../../../hooks/api/v2/stockList/useStockList";
import { useStorefrontTagList } from "../../../../../hooks/api/v2/storefrontTags/useStorefrontTagList";
import { useSellarSupplier } from "../../../../../hooks/useSellarSupplier";
import { StorefrontUpdateProductTagModal } from "./StorefrontUpdateProductTagModal";
import { StorefrontEditTagNameDialog } from "../StorefrontDialogs/StorefrontEditTagNameDialog";
import { BulkProductAssignmentDialog } from "./BulkProductAssignmentDialog";
import { DeleteGroupDialog } from "./DeleteGroupDialog";
import { SortableProductListItem } from "./SortableProductListItem";

type DragDropProviderProps = ComponentProps<typeof DragDropProvider>;

const reorder = <T,>(list: T[], startIndex: number, endIndex: number): T[] => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  if (!removed) return list;
  result.splice(endIndex, 0, removed);
  return result;
};

export const StorefrontLayoutGroupDetail = ({
  groupId,
}: {
  groupId: number;
}): ReactElement => {
  const navigate = useNavigate();
  const supplier = useSellarSupplier();
  const [renamedGroupName, setRenamedGroupName] = useState<string>();
  const [renameDialogActive, setRenameDialogActive] = useState(false);
  const [addProductsDialogActive, setAddProductsDialogActive] = useState(false);
  const [moveProductsDialogActive, setMoveProductsDialogActive] =
    useState(false);
  const [movingProductId, setMovingProductId] = useState<number>();
  const [deleteMoveDialogActive, setDeleteMoveDialogActive] = useState(false);
  const [deleteDialogActive, setDeleteDialogActive] = useState(false);
  const tagsQuery = useStorefrontTagList({
    query: [{ type: "SupplierId", value: supplier.id, modifier: "EQUALS" }],
  });
  const productsQuery = useStockList({
    ParentId: null,
    SupplierId: supplier.id,
    StorefrontTagId: groupId,
  });
  const ungroupedProductsQuery = useProductUngrouped();
  const reorderMutation = useProductReOrder();
  const group = tagsQuery.data?.pages.flat().find((tag) => tag.id === groupId);
  const productsFromQuery = useMemo(
    () => productsQuery.data?.pages.flat(),
    [productsQuery.data?.pages],
  );
  const [productList, setProductList] = useState<StockListResponseType>(
    () => productsFromQuery ?? [],
  );
  const [previousProducts, setPreviousProducts] = useState(productsFromQuery);

  if (productsFromQuery !== previousProducts) {
    setPreviousProducts(productsFromQuery);
    setProductList(productsFromQuery ?? []);
  }

  const onDragEnd: DragDropProviderProps["onDragEnd"] = (event) => {
    if (event.canceled) return;
    const active = event.operation.source;
    const productId = Number(active?.id);
    const sortableIndexes = getDndKitSortableIndexes(active);
    if (!active || !Number.isInteger(productId) || !sortableIndexes) return;

    const { oldIndex, newIndex } = sortableIndexes;
    if (
      oldIndex === newIndex ||
      !productList[oldIndex] ||
      !productList[newIndex]
    ) {
      return;
    }

    const previousList = productList;
    setProductList(reorder(productList, oldIndex, newIndex));

    const hasMovedDown = oldIndex < newIndex;
    const beforeLexoRank =
      productList[hasMovedDown ? newIndex : newIndex - 1]?.lexoRank ?? null;
    const isLastProduct = !productList[newIndex + 1];
    const afterLexoRank = isLastProduct
      ? null
      : (productList[hasMovedDown ? newIndex + 1 : newIndex]?.lexoRank ?? null);

    reorderMutation.mutate(
      { productId, beforeLexoRank, afterLexoRank },
      { onError: () => setProductList(previousList) },
    );
  };

  const isLoading = tagsQuery.isLoading || productsQuery.isLoading;
  const isError = tagsQuery.isError || productsQuery.isError;
  const groupName = renamedGroupName ?? group?.value ?? "Group";
  const movingProduct = productList.find(
    (product) => product.id === movingProductId,
  );
  const totalGroupProducts = group?.ProductStorefrontTag.length ?? 0;
  const canDelete = totalGroupProducts === 0;

  return (
    <>
      <NavigationBar
        title="Layout & Grouping"
        leftIcon="CHEVRON_LEFT"
        onClickLeftIcon={() => navigate(LAYOUT_GROUPING)}
      />
      <main className="mx-auto flex w-full max-w-230 items-center gap-2">
        <div className="mt-2 mb-24 flex w-full min-w-0 flex-col gap-8 px-4 xl:mt-10">
          {isLoading ? (
            <SpinnerLegacy />
          ) : isError ? (
            <Alert
              type="error"
              title="This group could not be loaded"
              message="Try loading this group again or return to Layout & Grouping."
            >
              <DataButton
                border={true}
                onClick={() => {
                  void tagsQuery.refetch();
                  void productsQuery.refetch();
                }}
              >
                Try again
              </DataButton>
              <DataButton
                border={true}
                onClick={() => navigate(LAYOUT_GROUPING)}
              >
                Back to groups
              </DataButton>
            </Alert>
          ) : !group ? (
            <Alert
              type="warning"
              title="Group not found"
              message="This group may have been deleted or is no longer available."
            >
              <DataButton
                border={true}
                onClick={() => navigate(LAYOUT_GROUPING)}
              >
                Back to groups
              </DataButton>
            </Alert>
          ) : (
            <>
              <header className="flex flex-col gap-4 border-b border-zinc-300 pb-4">
                <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
                  <div className="flex min-w-0 flex-col gap-1">
                    <H4 className="truncate font-semibold">{groupName}</H4>
                    <p className="text-zinc-500">
                      {productList.length}{" "}
                      {pluralize("product", productList.length)} in this group
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <DataButton
                      border={true}
                      disabled={productList.length === 0}
                      onClick={() => setMoveProductsDialogActive(true)}
                    >
                      <Icon icon="SWAP_HORIZONTAL" />
                      Move products
                    </DataButton>
                    <DataButton
                      border={true}
                      onClick={() => setRenameDialogActive(true)}
                    >
                      <Icon icon="PENCIL" />
                      Rename
                    </DataButton>
                    <DataButton
                      border={true}
                      title={
                        canDelete
                          ? `Delete ${groupName}`
                          : `Move products out of ${groupName}, then delete it`
                      }
                      className="text-red-700"
                      onClick={() =>
                        canDelete
                          ? setDeleteDialogActive(true)
                          : setDeleteMoveDialogActive(true)
                      }
                    >
                      <Icon icon="DELETE" />
                      Delete
                    </DataButton>
                  </div>
                </div>
              </header>

              <section className="flex flex-col gap-4">
                <div className="flex min-w-0 items-start justify-between gap-4">
                  <div className="flex min-w-0 flex-col gap-1">
                    <H5>Products</H5>
                    <span className="text-zinc-500">
                      Set the order customers see within this group.
                    </span>
                  </div>
                  <DataButton
                    border={true}
                    className="shrink-0"
                    onClick={() => setAddProductsDialogActive(true)}
                  >
                    <Icon icon="PLUS" />
                    Add products
                  </DataButton>
                </div>

                {reorderMutation.isError && (
                  <Alert
                    type="error"
                    title="The product order was not saved"
                    message="The previous order has been restored. Try moving the product again."
                    onDismiss={() => reorderMutation.reset()}
                  />
                )}

                {productList.length === 0 ? (
                  <Alert
                    type="information"
                    title="This group has no products"
                    message="Add products to make the group visible and useful on your storefront."
                  >
                    <DataButton
                      border={true}
                      onClick={() => setAddProductsDialogActive(true)}
                    >
                      Add products
                    </DataButton>
                  </Alert>
                ) : (
                  <DragDropProvider onDragEnd={onDragEnd}>
                    <div
                      className="flex flex-col gap-2"
                      aria-busy={reorderMutation.isPending}
                    >
                      {productList.map((product, index) => (
                        <SortableProductListItem
                          key={product.id}
                          product={product}
                          onMove={() => setMovingProductId(product.id)}
                          index={index}
                          disabled={reorderMutation.isPending}
                        />
                      ))}
                    </div>
                  </DragDropProvider>
                )}
                {productsQuery.hasNextPage && (
                  <DataButton
                    border
                    disabled={productsQuery.isFetchingNextPage}
                    onClick={() => void productsQuery.fetchNextPage()}
                  >
                    Load more products
                  </DataButton>
                )}
              </section>
            </>
          )}
        </div>
      </main>

      <StorefrontEditTagNameDialog
        storefrontTagId={groupId}
        active={renameDialogActive}
        onDismiss={() => setRenameDialogActive(false)}
        selectedTagName={groupName}
        setSelectedTagName={setRenamedGroupName}
      />
      {addProductsDialogActive && (
        <BulkProductAssignmentDialog
          active
          onDismiss={() => setAddProductsDialogActive(false)}
          products={ungroupedProductsQuery.data ?? []}
          fixedDestination={{ id: groupId, name: groupName }}
          title={`Add products to ${groupName}`}
        />
      )}
      {moveProductsDialogActive && (
        <BulkProductAssignmentDialog
          active
          onDismiss={() => {
            setMoveProductsDialogActive(false);
            setMovingProductId(undefined);
          }}
          products={productList}
          currentGroupId={groupId}
          currentGroupName={groupName}
          totalGroupProducts={totalGroupProducts}
          title={`Move products from ${groupName}`}
        />
      )}
      {movingProduct && (
        <StorefrontUpdateProductTagModal
          onDismiss={() => setMovingProductId(undefined)}
          productId={movingProduct.id}
          productName={movingProduct.name ?? "Unnamed product"}
          storefrontTagName={groupName}
          currentGroupId={groupId}
          isLastProduct={totalGroupProducts === 1}
        />
      )}
      {deleteMoveDialogActive && (
        <BulkProductAssignmentDialog
          active
          onDismiss={() => setDeleteMoveDialogActive(false)}
          products={productList}
          currentGroupId={groupId}
          currentGroupName={groupName}
          totalGroupProducts={totalGroupProducts}
          moveAllProducts
          title={`Delete ${groupName}?`}
        />
      )}
      {deleteDialogActive && (
        <DeleteGroupDialog
          groupName={groupName}
          active
          onDismiss={() => setDeleteDialogActive(false)}
          groupId={groupId}
        />
      )}

      <span className="sr-only" aria-live="polite">
        {reorderMutation.isPending ? "Saving product order" : ""}
      </span>
    </>
  );
};
